import { useEffect } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/contexts/AuthContext";

interface RouteGuardProps {
  children: React.ReactNode;
}

export const RouteGuard = ({ children }: RouteGuardProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const publicRoutes = ["/login", "/signup"];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  useEffect(() => {
    if (!loading) {
      if (!user && !isPublicRoute) {
        // User is not authenticated and trying to access protected route
        navigate({ to: "/login" });
      } else if (user && isPublicRoute) {
        // User is authenticated but on login/signup page
        navigate({ to: "/" });
      }
    }
    // Include the pathname so redirects trigger when navigating between routes
    // that share the same public/protected status
  }, [user, loading, isPublicRoute, navigate, location.pathname]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render children if user should be redirected
  if (!loading && !user && !isPublicRoute) {
    return null;
  }

  // Don't render children if authenticated user is on auth pages
  if (!loading && user && isPublicRoute) {
    return null;
  }

  return <>{children}</>;
};
